import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, tzHeadersSchema, coerceBooleanFromString } from '../schemas';

// ============================================================
// Field Schemas
// ============================================================

/**
 * `YYYY-MM-DD`, not the backend's `DD.MM.YYYY` `calendarDateField`. This is the
 * one contract in the service written for a caller that is not ours, and ISO
 * ordering is what every external planning system emits. The service converts at
 * its boundary (`ExternalActivityService.toDate`), so the difference stops here.
 */
const externalDateField = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD format');

/**
 * The caller's own identity for the task. Nested, and deliberately carries no
 * `projectId` at any depth: the project is the path's `:projectId`, which is the
 * value `assertProjectMembership` actually verified. A project inside a nested
 * body object would be invisible to `collectRequestProjectIds` — a scope claim
 * nothing checks.
 */
const externalRefBodySchema = z.object({
	system: requiredStringSchema.describe('The external system that owns the task, e.g. "d-orca"'),
	siteId: requiredStringSchema.describe("The external system's own site identifier"),
	taskId: requiredStringSchema.describe("The task's id within the external system"),
});

// ============================================================
// Param / Query Schemas
// ============================================================

const projectParamsSchema = z.object({
	projectId: requiredStringSchema.describe('Project identifier'),
});

/**
 * `(system, taskId)` addresses the task, matching the unique index. `siteId` is
 * NOT in the path: it is traceability data, and putting it here would make the
 * same task addressable under two spellings of its site.
 */
const externalRefParamsSchema = projectParamsSchema.extend({
	system: requiredStringSchema.describe('The external system that owns the task'),
	taskId: requiredStringSchema.describe("The task's id within the external system"),
});

const deleteExternalActivityQuerySchema = z.object({
	force: coerceBooleanFromString.optional().describe('Delete even when the activities have accumulated work time'),
});

/**
 * Both filters are optional and both narrow WITHIN the path's project — they are
 * filters on a collection, never the thing that decides which project is read.
 *
 * `taskIds` is a comma-separated list, matching `floorIds`/`areaIds` elsewhere in
 * the fleet rather than inventing a second convention for repeated query keys.
 */
const listExternalActivitiesQuerySchema = z.object({
	system: z.string().min(1).optional().describe('Only tasks owned by this external system'),
	taskIds: z.string().min(1).optional().describe('Comma-separated task ids to scope the read to'),
});

// ============================================================
// Body Schemas
// ============================================================

/**
 * No `projectId`: the path carries it and it is stamped server-side on insert.
 * No `sequenceId` either — an external caller does not know, and must not choose,
 * which Trusstor sequence its task lands in; the service resolves the project's
 * master sequence for its `system`.
 */
const createExternalActivityBodySchema = z.object({
	externalRef: externalRefBodySchema,
	description: requiredStringSchema.describe('What the task is'),
	professionId: requiredStringSchema.describe('Trusstor profession id, must be reachable in this project'),
	areaIds: z.array(requiredStringSchema).min(1).describe('Trusstor area ids — any areaType, any floor, at least one'),
	startDate: externalDateField.describe('Planned start, YYYY-MM-DD'),
	endDate: externalDateField.describe('Planned end, YYYY-MM-DD'),
	assignee: z.string().nullable().optional().describe('Trusstor username'),
	dangerousActivity: z.boolean().describe('Whether the task is a dangerous activity'),
	label: z.string().optional().describe('Free text; an existing label with this text is reused'),
	orderIndex: z.number().optional().describe('Defaults to the end of the external sequence'),
});

/**
 * Every field optional; `externalRef` is absent by construction because it is
 * immutable and addressed by the path. Omitting it from the schema is what
 * prevents it being sent, rather than merely ignored.
 */
const updateExternalActivityBodySchema = createExternalActivityBodySchema.omit({ externalRef: true }).partial();

// ============================================================
// Response Schemas
// ============================================================

/**
 * The envelope is declared exactly; the domain objects inside are `z.any()`.
 *
 * That is not laziness — `customSerializerCompiler` PARSES the response, and Zod
 * strips keys a schema does not declare, so an approximate schema for a merged
 * area sequence item would silently delete fields from the payload rather than
 * merely under-describe it. There is no shared Zod schema for the merged shape
 * (`IMergedAreaSequenceItemShared` is an interface), and the rest of this
 * service's ASI/group/activity responses are typed the same way for the same
 * reason.
 */
const externalActivityResultSchema = z.object({
	sequenceItemId: z.string(),
	groupId: z.string(),
	activityIds: z.array(z.string()),
	areaSequenceItems: z.array(z.any()).describe('IMergedAreaSequenceItem[]'),
});

/**
 * One element means the same thing whether it came from the per-task read or the
 * list, `externalRef` included — so a caller can key the list by `taskId` without
 * relying on response order.
 */
const externalActivityViewSchema = z.object({
	externalRef: externalRefBodySchema,
	sequenceItemId: z.string(),
	// Declared, or the serializer strips it: the group's profession on the element
	// is what lets the viewer response filter drop the element as a whole.
	profession: z.any().describe('IMinimizedProfession'),
	group: z.any().describe('IActivitiesGroup'),
	activities: z.array(z.any()).describe('IAssignedActivity[]'),
	areaSequenceItems: z.array(z.any()).describe('IMergedAreaSequenceItem[]'),
});

// ============================================================
// Contract
// ============================================================

/**
 * One fully-linked activity per externally-owned task, keyed by the caller's own
 * task id: `docs/plans/2026-09-17-external-activities-api-design.md`.
 *
 * Project scope, the ordinary kind — `/projects/:projectId/externalActivities`,
 * central `assertProjectMembership`, and every read and write below is scoped to
 * that project inside the service, so a task id belonging to another project is a
 * 404 rather than a 403.
 *
 * `{ root: true }` so the project can precede the collection segment.
 *
 * Route order matters: the literal collection POST is declared before the
 * parameterized `/:system/:taskId` routes.
 */
export const externalActivityContract = defineContract(
	'externalActivities',
	{
		createExternalActivity: route({
			method: 'POST',
			path: '/projects/:projectId/externalActivities',
			params: projectParamsSchema,
			body: createExternalActivityBodySchema,
			headers: tzHeadersSchema,
			response: externalActivityResultSchema,
			operationId: 'createExternalActivity',
			tags: ['External Activities'],
			summary: 'Create a fully-linked activity for an external task',
			description:
				'Creates the sequence item, its area sequence items, the activities group and one activity per floor in one call. Idempotent on (projectId, externalRef.system, externalRef.taskId): a repeated call returns the existing records.',
		}),
		/**
		 * Declared before `/:system/:taskId` — the collection read is the shorter path
		 * and must not be shadowed by the parameterized one.
		 *
		 * No `project-id` or `tz` header. The project is the path param, which is the
		 * value the membership guard verifies; a header would be a second, unverified
		 * way to say which project to read. There is no date conversion on the way out,
		 * so there is nothing for a timezone to do here.
		 */
		listExternalActivities: route({
			method: 'GET',
			path: '/projects/:projectId/externalActivities',
			params: projectParamsSchema,
			query: listExternalActivitiesQuerySchema,
			response: z.array(externalActivityViewSchema),
			operationId: 'listExternalActivities',
			tags: ['External Activities'],
			summary: 'List the live external activities of a project',
			description:
				'Returns every external activity of the project that has a live activities group, optionally filtered by owning system and/or task ids. Unknown task ids are absent rather than an error.',
		}),
		getExternalActivity: route({
			method: 'GET',
			path: '/projects/:projectId/externalActivities/:system/:taskId',
			params: externalRefParamsSchema,
			response: externalActivityViewSchema,
			operationId: 'getExternalActivity',
			tags: ['External Activities'],
			summary: 'Get the linked records of an external task',
			description:
				'Returns the activities group, its activities and the area sequence items, for reconciliation.',
		}),
		updateExternalActivity: route({
			method: 'PUT',
			path: '/projects/:projectId/externalActivities/:system/:taskId',
			params: externalRefParamsSchema,
			body: updateExternalActivityBodySchema,
			headers: tzHeadersSchema,
			response: externalActivityResultSchema,
			operationId: 'updateExternalActivity',
			tags: ['External Activities'],
			summary: 'Update an external task',
			description:
				'Group fields go through the ordinary group-update path; areaIds reconciles the area sequence items, keeping existing ones and their statuses.',
		}),
		deleteExternalActivity: route({
			method: 'DELETE',
			path: '/projects/:projectId/externalActivities/:system/:taskId',
			params: externalRefParamsSchema,
			query: deleteExternalActivityQuerySchema,
			// The delete itself needs no timezone; the workplan stream refresh that
			// follows it does, exactly as `DELETE /groups/:groupId` does.
			headers: tzHeadersSchema,
			response: z.string(),
			operationId: 'deleteExternalActivity',
			tags: ['External Activities'],
			summary: 'Delete an external task and everything linked to it',
			description:
				'Deletes the activities group (resetting its area sequence items), its activities, and the sequence item with its area sequence items. Refuses with 409 when the activities have accumulated work time, unless force=true.',
		}),
	},
	{ root: true }
);
